import { useState } from 'react'
import { useTexte } from '../../texte'

import { BLASENFARBEN, type Blasenfarbe } from '../../lib/blasenfarben'
import { schriftAuf } from '../../lib/kontrast'

/** Ein vollständiger Satz Blasenfarben — leer heißt „wie das Erscheinungsbild". */
export type Blasensatz = Record<Blasenfarbe, string>

/**
 * Zwei Farbfelder, zwei Beispielblasen, ein Speichern-Knopf.
 *
 * **Das sichtbare Stück von zwei Karten**: der Voreinstellung in der
 * Verwaltung und der Farbe einer einzelnen Reise (#49). Beide zeigen dasselbe,
 * und sie sollen es weiter tun — zwei Abschriften desselben Formulars laufen
 * in diesem Projekt erfahrungsgemäß nach wenigen Änderungen auseinander.
 *
 * **Leer heißt „wie das Erscheinungsbild".** Das ist der wichtige Zustand:
 * eine einmal gesetzte helle Farbe gälte sonst auch in der dunklen
 * Darstellung, und eine weiße Blase auf dunklem Grund wäre eine Sackgasse.
 * Der Knopf *Automatisch* führt zurück.
 *
 * Der Entwurf liegt hier und nicht beim Aufrufer: was man sieht, ist erst
 * gespeichert, wenn man speichert.
 */
export function Blasenwahl({
  lead,
  werte,
  ererbt,
  leerText,
  laeuft,
  fehler,
  gemeldet,
  voreinstellung,
  onSpeichern,
}: {
  lead: string
  werte: Blasensatz
  /**
   * **Was gilt, solange hier nichts steht** — für das Farbfeld.
   *
   * Ein Farbfeld kennt kein „leer": es zeigt immer eine Farbe. Welche, ist die
   * Frage, und die Antwort ist die nächste Stufe der Kette. Aus dem Betrieb
   * gemeldet: beim Mitlesenden stand dort die allgemeine Voreinstellung,
   * obwohl in seiner Reise die Farbe des **Autors** gilt — das Feld zeigte
   * eine Farbe, die nirgends galt.
   *
   * Fehlt die Angabe, bleibt es beim eingebauten Wert des Erscheinungsbilds
   * (`BLASENFARBEN[].vorgabe`) — und genau der gilt dann ja auch.
   */
  ererbt?: Partial<Blasensatz>
  /**
   * Was der Knopf neben jeder Zeile heißt, der den Wert wieder wegräumt.
   *
   * **Weil „leer" nicht überall dasselbe heißt** (#52): in der Verwaltung und
   * an der Reise heißt es „wie das Erscheinungsbild", beim Mitlesenden „wie
   * der Autor". Der Wert ist derselbe; was er bedeutet, entscheidet die
   * Kette — und die Aufschrift sagt es, statt dass zweimal dasselbe Wort für
   * zwei Dinge steht.
   */
  leerText?: string
  laeuft: boolean
  fehler: string | null
  gemeldet: boolean
  /**
   * Nur an der Reise: die Voreinstellung aus den allgemeinen Einstellungen,
   * zum Übernehmen. Fehlt sie, steht der Knopf nicht da — in der Verwaltung
   * *ist* man die Voreinstellung.
   */
  voreinstellung?: Blasensatz
  /**
   * Speichern. **Ein Versprechen und kein Rückruf ins Leere**: erst wenn es
   * eingelöst ist, wird der Entwurf hier weggeräumt — sonst stünde nach einem
   * fehlgeschlagenen Speichern ein Wert im Feld, den niemand hat.
   */
  onSpeichern: (werte: Blasensatz) => Promise<void>
}) {
  const t = useTexte()
  const [entwurf, setEntwurf] = useState<Partial<Blasensatz>>({})

  const gilt = (welche: Blasenfarbe) => entwurf[welche] ?? werte[welche] ?? ''
  const jetzt = (): Blasensatz => ({
    chat_own_color: gilt('chat_own_color'),
    chat_other_color: gilt('chat_other_color'),
  })
  const geaendert = BLASENFARBEN.some((e) => gilt(e.key) !== (werte[e.key] ?? ''))
  const wieVoreinstellung =
    voreinstellung !== undefined &&
    BLASENFARBEN.every((e) => gilt(e.key) === (voreinstellung[e.key] ?? ''))

  function setze(welche: Blasenfarbe, wert: string) {
    setEntwurf((alt) => ({ ...alt, [welche]: wert }))
  }

  /**
   * Die Beispielblase zeigt dasselbe wie das Farbfeld daneben.
   *
   * Steht hier nichts, zeigt sie die geerbte Farbe — und ohne die gar keine,
   * denn dann gilt das Erscheinungsbild, und das kennt nur der Browser.
   */
  function vorschau(welche: Blasenfarbe) {
    const farbe = gilt(welche) === '' ? (ererbt?.[welche] ?? '') : gilt(welche)
    return farbe === ''
      ? undefined
      : { background: farbe, color: schriftAuf(farbe) }
  }

  return (
    <>
      <p className="card__lead card__lead--absatz">
        {lead}
      </p>

      {BLASENFARBEN.map((eintrag) => (
        <div key={eintrag.key} className="chatfarbe">
          {/* Ein natives Farbfeld: auf dem iPhone öffnet das den Farbwähler
              des Systems, und der kann mehr, als ich hier je nachbauen
              sollte. */}
          <input
            type="color"
            className="kartenregler__farbe"
            name={eintrag.key}
            aria-label={t.farben.blasenFelder[eintrag.key].titel}
            // Ein Farbfeld kennt kein „leer". Steht nichts, zeigt es die
            // Farbe, die gerade gilt -- und die weiß nur der Browser.
            value={
              gilt(eintrag.key) === ''
                ? (ererbt?.[eintrag.key] || eintrag.vorgabe)
                : gilt(eintrag.key)
            }
            onChange={(event) => setze(eintrag.key, event.target.value)}
          />
          <span className="chatfarbe__text">
            <strong>{t.farben.blasenFelder[eintrag.key].titel}</strong>
            <span className="chatfarbe__hinweis">{t.farben.blasenFelder[eintrag.key].hinweis}</span>
          </span>
          <button
            type="button"
            className="button button--outline button--small"
            disabled={gilt(eintrag.key) === ''}
            onClick={() => setze(eintrag.key, '')}
          >
            {leerText ?? t.farben.automatisch}
          </button>
        </div>
      ))}

      {/* Die Vorschau. Sie trägt die Farben als eigene Angabe und nicht über
          die Variablen am Wurzelelement: solange nicht gespeichert ist, soll
          sich nur dieses Beispiel ändern und nicht die halbe Anwendung. */}
      <div className="chatvorschau">
        <div className="bubble-row">
          <div
            className="bubble"
            style={vorschau('chat_other_color')}
          >
            {t.farben.blasenBeispielAndere}
          </div>
        </div>
        <div className="bubble-row bubble-row--own">
          <div
            className="bubble"
            style={vorschau('chat_own_color')}
          >
            {t.farben.blasenBeispielEigen}
          </div>
        </div>
      </div>

      {fehler !== null && (
        <p className="notice" role="alert">
          {fehler}
        </p>
      )}
      {gemeldet && !laeuft && !geaendert && (
        <p className="card__lead akzent">{t.allgemein.gespeichert}</p>
      )}

      <div className="chatfarbe__knoepfe">
        <button
          className="button button--outline"
          type="button"
          disabled={!geaendert || laeuft}
          onClick={() => {
            void onSpeichern(jetzt())
              .then(() => setEntwurf({}))
              .catch(() => undefined)
          }}
        >
          {laeuft ? t.allgemein.laeuft : t.allgemein.speichern}
        </button>

        {/* **Der Knopf füllt den Entwurf, er speichert nicht.** So bleibt es
            bei einem einzigen schreibenden Weg, man sieht an den zwei
            Beispielblasen, was man bekommt, und ein versehentliches Antippen
            kostet nichts. */}
        {voreinstellung !== undefined && (
          <button
            className="button button--outline"
            type="button"
            disabled={laeuft || wieVoreinstellung}
            onClick={() =>
              setEntwurf({
                chat_own_color: voreinstellung.chat_own_color ?? '',
                chat_other_color: voreinstellung.chat_other_color ?? '',
              })
            }
          >
            {t.farben.voreinstellungUebernehmen}
          </button>
        )}
      </div>
    </>
  )
}
